import React, { useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { useStudents, useCreateStudent, useUpdateStudent, useDeleteStudent, useStudentAnalytics, useStudentFormData } from '../../hooks/useStudents';
import { useAuth, useUserPermissions } from '../../hooks/useAuth';
import { useMyFieldPermissions } from '../../hooks/usePermissions';
import StudentTable from './StudentTable';
import StudentForm from './StudentForm';
import StudentRegistrationForm from './StudentRegistrationForm';
import BulkUploadModal from './BulkUploadModal';
import FilterModal from './FilterModalV2';
import { ExportDialog } from './ExportDialog';
import {
  Plus,
  Search,
  Users,
  AlertCircle,
  Loader2,
  Upload,
  Download,
  Filter,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

// Pagination Controls Component - Reusable for top and bottom
const PaginationControls = ({
  currentPage,
  totalPages,
  pageSize,
  totalCount,
  onNextPage,
  onPreviousPage,
  onPageSizeChange,
  showPageSizeSelector = false,
  t
}) => {
  const pageSizeOptions = [25, 50, 100];
  const from = (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t bg-card">
      <div className="flex items-center gap-4">
        <div className="text-sm text-muted-foreground">
          {t('pagination.showing', {
            from,
            to,
            total: totalCount
          })}
        </div>

        {showPageSizeSelector && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{t('pagination.show', 'Show')}:</span>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => onPageSizeChange(parseInt(value))}
            >
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">{t('pagination.perPage', 'per page')}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPreviousPage}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          {t('pagination.previous')}
        </Button>

        <div className="text-sm text-muted-foreground">
          {t('pagination.pageOf', { current: currentPage, total: totalPages })}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={onNextPage}
          disabled={currentPage === totalPages}
        >
          {t('pagination.next')}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

const StudentsPage = () => {
  const { t } = useTranslation();
  useAuth();
  const { hasPermission, userType } = useUserPermissions();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get field permissions for filter visibility and export
  const { data: fieldPermissions } = useMyFieldPermissions();
  const filterPermissions = fieldPermissions?.filter_permissions || {};
  const availableFields = fieldPermissions?.fields || [];

  // Derive filter visibility from permissions
  const showInterviewFilter = filterPermissions.interview_score ?? false;
  const showPaymentFilter = filterPermissions.payment_fields ?? false;
  const showUploadFilter = filterPermissions.upload_status ?? true; // Default to true for backwards compat

  const searchDebounceRef = useRef(null);

  // UI state (not persisted to URL)
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isRegistrationFormOpen, setIsRegistrationFormOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [studentToCancel, setStudentToCancel] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);
  const [showExportDialog, setShowExportDialog] = useState(false);

  // Filter state from URL (persisted)
  const searchTerm = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || 'ACTIVE';
  const courseFilter = searchParams.get('course') || '';
  const cinnireFilter = searchParams.get('cinnire') || '';
  const hasFriendsFilter = searchParams.get('hasFriends') || '';
  const notesFilter = searchParams.get('notes') || '';
  const uploadStatusFilter = searchParams.get('uploadStatus') || '';
  const interviewStageFilter = searchParams.get('interviewStage') || '';
  const paymentStageFilter = searchParams.get('paymentStage') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || '50', 10);
  const orderBy = searchParams.get('orderBy') || 'student_id';
  const orderDirection = searchParams.get('orderDirection') || 'asc';

  // Helper function to update URL params
  const updateSearchParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, value.toString());
      }
    });
    setSearchParams(newParams);
  };


  // API integration
  const {
    data: studentsResponse,
    isLoading: studentsLoading,
    error: studentsError,
    refetch: refetchStudents
  } = useStudents({
    search: searchTerm,
    status: statusFilter || undefined, // Don't pass empty string, pass undefined for "all"
    course: courseFilter || undefined,
    is_cinnire: cinnireFilter || undefined,
    has_friends: hasFriendsFilter || undefined,
    has_notes: notesFilter || undefined,
    upload_status: uploadStatusFilter || undefined,
    interview_stage: interviewStageFilter || undefined,
    payment_stage: paymentStageFilter || undefined,
    page: currentPage,
    page_size: pageSize,
    ordering: orderDirection === 'desc' ? `-${orderBy}` : orderBy,
  });

  // Analytics for stats strip
  useStudentAnalytics();

  // Get form data for filter options
  const { courses, isLoading: coursesLoading } = useStudentFormData();

  const createStudentMutation = useCreateStudent();
  const updateStudentMutation = useUpdateStudent();
  const deleteStudentMutation = useDeleteStudent();

  // Extract students and pagination from API response
  const students = studentsResponse?.results || [];
  const totalCount = studentsResponse?.count || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Students are already filtered by the API, so we just use them directly
  const filteredStudents = students;

  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      updateSearchParams({ page: currentPage + 1 });
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      updateSearchParams({ page: currentPage - 1 });
    }
  };

  const handlePageSizeChange = (newSize) => {
    updateSearchParams({ pageSize: newSize, page: 1 }); // Reset to page 1 when changing page size
  };

  const handleSort = (field) => {
    if (orderBy === field) {
      // Toggle direction if clicking same field
      updateSearchParams({ orderDirection: orderDirection === 'asc' ? 'desc' : 'asc' });
    } else {
      // New field, default to ascending
      updateSearchParams({ orderBy: field, orderDirection: 'asc' });
    }
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter && statusFilter !== 'ACTIVE') count++; // ACTIVE is default, so don't count it
    if (courseFilter) count++;
    if (cinnireFilter) count++;
    if (hasFriendsFilter) count++;
    if (notesFilter) count++;
    if (uploadStatusFilter) count++;
    if (interviewStageFilter) count++;
    if (paymentStageFilter) count++;
    return count;
  }, [statusFilter, courseFilter, cinnireFilter, hasFriendsFilter, notesFilter, uploadStatusFilter, interviewStageFilter, paymentStageFilter]);

  // Get filter label
  const getFilterLabel = (type, value) => {
    if (!value) return null;

    switch (type) {
      case 'status':
        if (value === 'ACTIVE') return null; // Don't show default
        return t(`status.${value}`, value);
      case 'course':
        const course = courses?.find(c => c.uuid === value);
        return course ? course.name : value;
      case 'cinnire':
        return value === 'true' ? 'Cinnirí Only' : 'Non-Cinnirí';
      case 'notes':
        return value === 'true' ? t('filters.notes.hasNotes', 'Has Notes') : t('filters.notes.noNotes', 'No Notes');
      case 'upload':
        if (value === 'HAS_ISSUES') return t('students.uploadStatus.withIssues', 'With Issues (Both)');
        if (value === 'IMPORT_WARNING') return t('students.uploadStatus.withWarnings', 'Warnings Only');
        if (value === 'IMPORT_ERROR') return t('students.uploadStatus.withErrors', 'Errors Only');
        if (value === 'ACTION_REQUIRED') return t('students.uploadStatus.actionRequired', 'Action Required');
        return value;
      case 'interview':
        if (value === 'scored') return t('filters.interview.scored', 'Scored');
        if (value === 'not_scored') return t('filters.interview.notScored', 'Not Scored');
        if (value.startsWith('score_')) {
          const score = value.split('_')[1];
          return `${t('filters.interview.score', 'Score')} ${score}`;
        }
        return value;
      case 'payment':
        const paymentLabels = {
          'fully_paid': t('filters.payment.fullyPaid', 'Fully Paid'),
          'outstanding': t('filters.payment.outstanding', 'Outstanding Balance'),
          'no_payments': t('filters.payment.noPayments', 'No Payments'),
          'refunded': t('filters.payment.refunded', 'Refunded'),
          'deposit_only': t('filters.payment.depositOnly', 'Deposit Only')
        };
        return paymentLabels[value] || value;
      default:
        return value;
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    updateSearchParams({
      status: 'ACTIVE',
      course: '',
      cinnire: '',
      hasFriends: '',
      notes: '',
      uploadStatus: '',
      interviewStage: '',
      paymentStage: '',
      page: 1
    });
  };

  // API error handling - error details logged for debugging
  if (studentsError) {
    // Error details: status, statusText, data, message available for debugging
  }

  const handleAddStudent = () => {
    setEditingStudent(null);
    setIsRegistrationFormOpen(true);
  };

  const handleBulkUpload = () => {
    setIsBulkUploadOpen(true);
  };

  const handleBulkUploadComplete = (results) => {
    // Refresh students list
    refetchStudents();

    // Show success toast
    toast({
      title: t('bulkUpload.steps.results.title'),
      description: t('bulkUpload.steps.results.subtitle', {
        count: results.successful + results.with_warnings + results.with_errors,
        total: results.total_rows
      }),
      variant: 'success',
    });
  };

  const handleEditStudent = (student) => {
    setEditingStudent(student);
    setIsFormOpen(true);
  };

  const handleDeleteStudent = (student) => {
    setStudentToCancel(student);
    setCancelDialogOpen(true);
  };

  const confirmCancelStudent = async () => {
    if (!studentToCancel) return;

    const studentName = `${studentToCancel.first_name} ${studentToCancel.last_name}`;

    try {
      await deleteStudentMutation.mutateAsync(studentToCancel.uuid);
      toast({
        title: t('students.cancelled', 'Student Cancelled'),
        description: t('students.cancelSuccess', { studentName }),
        variant: 'success',
      });
      setCancelDialogOpen(false);
      setStudentToCancel(null);

      // Refetch students to update the list
      await refetchStudents();
    } catch (error) {
      toast({
        title: t('common.error', 'Error'),
        description: t('students.cancelError', 'Failed to cancel student. Please try again.'),
        variant: 'destructive',
      });
    }
  };

  const handleViewStudent = (student) => {
    navigate(`/students/${student.uuid}`);
  };

  const handleSubmitStudent = async (data) => {
    try {
      const studentName = `${data.first_name} ${data.last_name}`;
      if (editingStudent) {
        await updateStudentMutation.mutateAsync({ id: editingStudent.id, ...data });
        toast({
          title: t('students.updated'),
          description: t('students.updateSuccess', { studentName }),
          variant: 'success',
        });
      } else {
        await createStudentMutation.mutateAsync(data);
        toast({
          title: t('students.created'),
          description: t('students.createSuccess', { studentName }),
          variant: 'success',
        });
      }

      setIsFormOpen(false);
      setEditingStudent(null);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: editingStudent ? t('students.error.updateFailed') : t('students.error.createFailed'),
        variant: 'destructive',
      });
    }
  };

  const handleSubmitRegistration = async (data) => {
    try {
      await createStudentMutation.mutateAsync(data);

      // Force refetch of current students list to ensure new student appears
      await refetchStudents();

      toast({
        title: 'Registration completed',
        description: `${data.first_name} ${data.last_name} has been successfully registered for Coláiste Lurgan.`,
        variant: 'success',
      });
      setIsRegistrationFormOpen(false);
    } catch (error) {
      // Registration error handled by toast notification
      toast({
        title: 'Registration failed',
        description: error.response?.data?.message || error.message || 'Failed to complete registration. Please try again.',
        variant: 'destructive',
      });
      throw error; // Re-throw to be handled by the form
    }
  };

  const isLoading = studentsLoading || createStudentMutation.isPending || updateStudentMutation.isPending;

  // Handle API errors - but be more graceful about common issues
  const handleApiError = () => {
    // Check if this might just be an empty database (common in development)
    const isLikelyEmptyDatabase = studentsError?.response?.status === 500 || 
                                  studentsError?.response?.status === 404 ||
                                  studentsError?.message?.includes('No students found');

    if (isLikelyEmptyDatabase) {
      // Treat as successful but empty response
      return false; // Don't show error, show empty state instead
    }

    // For genuine errors (network, auth, etc.), show error page
    return studentsError?.response?.status >= 400 && studentsError?.response?.status !== 404 && studentsError?.response?.status !== 500;
  };

  if (studentsError && handleApiError()) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-destructive">{t('students.error.loadStudentsFailed')}</h3>
            <p className="text-muted-foreground mb-4">
              {studentsError?.message || t('students.error.loadMessage')}
            </p>
            <Button onClick={() => refetchStudents()} variant="outline">
              {t('students.error.tryAgain')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Compact Header */}
      <div className="flex justify-between items-center pb-4 border-b">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-semibold">{t('students.title')}</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground border-l pl-4">
            <Users className="h-4 w-4" />
            {studentsLoading ? (
              <div className="h-4 w-16 bg-muted animate-pulse rounded" />
            ) : (
              <span>
                <span className="font-medium text-foreground">{filteredStudents.length}</span>
                <span className="text-muted-foreground">/{totalCount}</span>
              </span>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={handleBulkUpload} disabled={isLoading}>
            <Upload className="mr-2 h-4 w-4" />
            {t('students.bulkUpload')}
          </Button>

          <Button onClick={handleAddStudent} disabled={isLoading}>
            {isLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            {t('students.newStudent')}
          </Button>

          <Button
            variant="outline"
            onClick={() => setShowExportDialog(true)}
            disabled={filteredStudents.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            {t('export.button')}
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-muted/30 rounded-lg p-3 space-y-2 mb-4">
        <div className="flex gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('students.searchPlaceholder')}
              defaultValue={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                clearTimeout(searchDebounceRef.current);
                searchDebounceRef.current = setTimeout(() => {
                  updateSearchParams({ search: value, page: 1 });
                }, 300);
              }}
              className="pl-10"
            />
          </div>

          {/* Filters Button */}
          <Button variant="outline" className="gap-2" onClick={() => setIsFilterModalOpen(true)}>
            <Filter className="h-4 w-4" />
            {t('filters.button', 'Filters')}
            {activeFilterCount > 0 && (
              <Badge variant="default" className="ml-1 px-1.5 py-0 text-xs h-5">
                {activeFilterCount}
              </Badge>
            )}
          </Button>

        </div>

        {/* Active Filter Pills */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2">
            {getFilterLabel('status', statusFilter) && (
              <Badge variant="secondary" className="gap-1.5 pl-2 pr-1">
                {getFilterLabel('status', statusFilter)}
                <button
                  onClick={() => updateSearchParams({ status: 'ACTIVE', page: 1 })}
                  className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {getFilterLabel('course', courseFilter) && (
              <Badge variant="secondary" className="gap-1.5 pl-2 pr-1">
                {getFilterLabel('course', courseFilter)}
                <button
                  onClick={() => updateSearchParams({ course: '', page: 1 })}
                  className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {getFilterLabel('cinnire', cinnireFilter) && (
              <Badge variant="secondary" className="gap-1.5 pl-2 pr-1">
                {getFilterLabel('cinnire', cinnireFilter)}
                <button
                  onClick={() => updateSearchParams({ cinnire: '', page: 1 })}
                  className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {getFilterLabel('notes', notesFilter) && (
              <Badge variant="secondary" className="gap-1.5 pl-2 pr-1">
                {getFilterLabel('notes', notesFilter)}
                <button
                  onClick={() => updateSearchParams({ notes: '', page: 1 })}
                  className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {getFilterLabel('upload', uploadStatusFilter) && (
              <Badge variant="secondary" className="gap-1.5 pl-2 pr-1">
                {getFilterLabel('upload', uploadStatusFilter)}
                <button
                  onClick={() => updateSearchParams({ uploadStatus: '', page: 1 })}
                  className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {getFilterLabel('interview', interviewStageFilter) && (
              <Badge variant="secondary" className="gap-1.5 pl-2 pr-1">
                {getFilterLabel('interview', interviewStageFilter)}
                <button
                  onClick={() => updateSearchParams({ interviewStage: '', page: 1 })}
                  className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {getFilterLabel('payment', paymentStageFilter) && (
              <Badge variant="secondary" className="gap-1.5 pl-2 pr-1">
                {getFilterLabel('payment', paymentStageFilter)}
                <button
                  onClick={() => updateSearchParams({ paymentStage: '', page: 1 })}
                  className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="h-6 px-2 text-xs"
            >
              {t('students.clearAll', 'Clear all')}
            </Button>
          </div>
        )}
      </div>

      {/* Student List - Takes remaining height */}
      <div className="flex-1 min-h-0">
        <div className="h-full">
          {/* Top Pagination Controls */}
          {totalPages > 1 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={totalCount}
              onNextPage={handleNextPage}
              onPreviousPage={handlePreviousPage}
              onPageSizeChange={handlePageSizeChange}
              showPageSizeSelector={true}
              t={t}
            />
          )}

          <StudentTable
            students={filteredStudents}
            onEdit={handleEditStudent}
            onDelete={handleDeleteStudent}
            onView={handleViewStudent}
            isLoading={studentsLoading}
            userPermissions={{
              userType,
              hasPermission,
              canViewParentInfo: hasPermission('view_parent_info') || ['ADMIN', 'OFFICE'].includes(userType)
            }}
            orderBy={orderBy}
            orderDirection={orderDirection}
            onSort={handleSort}
          />

          {/* Bottom Pagination Controls */}
          {totalPages > 1 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={totalCount}
              onNextPage={handleNextPage}
              onPreviousPage={handlePreviousPage}
              onPageSizeChange={handlePageSizeChange}
              showPageSizeSelector={false}
              t={t}
            />
          )}
        </div>
      </div>

      {/* Student Edit Form Dialog */}
      <StudentForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        student={editingStudent}
        onSubmit={handleSubmitStudent}
        isLoading={isLoading}
      />

      {/* Student Registration Form Dialog */}
      <StudentRegistrationForm
        open={isRegistrationFormOpen}
        onOpenChange={setIsRegistrationFormOpen}
        onSubmit={handleSubmitRegistration}
        isLoading={createStudentMutation.isPending}
      />

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        onComplete={handleBulkUploadComplete}
      />

      {/* Filter Modal */}
      <FilterModal
        open={isFilterModalOpen}
        onOpenChange={setIsFilterModalOpen}
        statusFilter={statusFilter}
        setStatusFilter={(value) => updateSearchParams({ status: value, page: 1 })}
        courseFilter={courseFilter}
        setCourseFilter={(value) => updateSearchParams({ course: value, page: 1 })}
        cinnireFilter={cinnireFilter}
        setCinnireFilter={(value) => updateSearchParams({ cinnire: value, page: 1 })}
        hasFriendsFilter={hasFriendsFilter}
        setHasFriendsFilter={(value) => updateSearchParams({ hasFriends: value, page: 1 })}
        notesFilter={notesFilter}
        setNotesFilter={(value) => updateSearchParams({ notes: value, page: 1 })}
        uploadStatusFilter={uploadStatusFilter}
        setUploadStatusFilter={(value) => updateSearchParams({ uploadStatus: value, page: 1 })}
        interviewStageFilter={interviewStageFilter}
        setInterviewStageFilter={(value) => updateSearchParams({ interviewStage: value, page: 1 })}
        paymentStageFilter={paymentStageFilter}
        setPaymentStageFilter={(value) => updateSearchParams({ paymentStage: value, page: 1 })}
        courses={courses}
        coursesLoading={coursesLoading}
        activeFilterCount={activeFilterCount}
        onClearAll={clearAllFilters}
        showUploadStatusFilter={showUploadFilter}
        showInterviewStageFilter={showInterviewFilter}
        showPaymentStageFilter={showPaymentFilter}
      />

      {/* Export Dialog */}
      <ExportDialog
        open={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        availableFields={availableFields}
        currentFilters={{
          search: searchTerm,
          status: statusFilter,
          course: courseFilter,
          is_cinnire: cinnireFilter,
          has_friends: hasFriendsFilter,
          has_notes: notesFilter,
          upload_status: uploadStatusFilter,
          interview_stage: interviewStageFilter,
          payment_stage: paymentStageFilter,
        }}
        filteredCount={totalCount}
      />

      {/* Cancel Student Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('students.cancel.title', 'Cancel Student Enrollment')}
            </DialogTitle>
            <DialogDescription>
              {t('students.cancel.message',
                'Are you sure you want to cancel {{studentName}}\'s enrollment? This action can be reversed later by changing their status.',
                { studentName: studentToCancel ? `${studentToCancel.first_name} ${studentToCancel.last_name}` : '' }
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmCancelStudent}
              disabled={deleteStudentMutation.isPending}
            >
              {deleteStudentMutation.isPending
                ? t('students.cancelling', 'Cancelling...')
                : t('students.cancelEnrollment', 'Cancel Enrollment')
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentsPage;